import type { ExportingMimeTypeValue, ExportingOptions, Options } from 'highcharts';
import type Highcharts from 'highcharts/highstock';

declare module 'highcharts' {
  interface Chart {
    exportChart: (options: ExportingOptions, chartOptions?: Options) => void;
    getSVG: (chartOptions?: Options) => string;
  }
}

type ExportChartImageParams = {
  chart: Highcharts.Chart;
  type: ExportingMimeTypeValue;
  filename: string;
  options: Highcharts.Options;
  exportingOptions?: Omit<Highcharts.ExportingOptions, 'type' | 'filename'>;
};

// Lazily pre-load jsPDF + svg2pdf so Highcharts offline-exporting skips the
// CDN fetch and Cairo is always registered as "HighchartsFont".
// Highcharts skips pdfFont loading when SVG text is ASCII-only, leaving
// text elements with font-family "HighchartsFont" that jsPDF cannot resolve
// unless we pre-register it here.
export const ensurePdfDeps = async (): Promise<void> => {
  if ((globalThis as Record<string, unknown>).jspdf) return;

  const [{ jsPDF }] = await Promise.all([import('jspdf'), import('svg2pdf.js')]);

  try {
    const res = await fetch('/fonts/Cairo.ttf');
    if (res.ok) {
      const blob = await res.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (typeof reader.result === 'string') {
            resolve(reader.result.split(',')[1]);
          } else {
            reject(new Error('font read failed'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Push a single "initialized" handler — runs on every new jsPDF instance
      (jsPDF as any).API.events.push([
        'initialized',
        function (this: any) {
          for (const style of ['normal', 'bold', 'italic', 'bolditalic']) {
            this.addFileToVFS(style, base64);
            this.addFont(style, 'HighchartsFont', style);
          }
          this.setFont('HighchartsFont');
        }
      ]);
    }
  } catch {
    // best-effort — PDF falls back to default fonts if Cairo is unavailable
  }

  (globalThis as Record<string, unknown>).jspdf = { jsPDF };
};

const svgToCanvas = (svg: string, width: number, height: number): Promise<HTMLCanvasElement> =>
  new Promise((resolve, reject) => {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(url);
        reject(new Error('Canvas 2D context unavailable'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('SVG render failed'));
    };
    img.src = url;
  });

// Highcharts' svg2pdf path silently drops chart content for non-stock chart
// instances. Bypass it by rendering the export SVG to a canvas (browser-native,
// which correctly handles all Highcharts series types) and embedding the
// resulting PNG in a jsPDF document.
export const exportChartAsPng = async (
  chart: Highcharts.Chart,
  chartOptions: Options,
  sourceWidth: number,
  filename: string
): Promise<void> => {
  await ensurePdfDeps();

  const rawSvg = chart.getSVG({
    ...chartOptions,
    chart: { ...chartOptions.chart, width: sourceWidth }
  });

  // Replace Highcharts' internal font alias so the browser uses Cairo when
  // rendering the SVG via canvas.
  const svg = rawSvg
    .replace(/font-family="HighchartsFont"/g, 'font-family="Cairo, sans-serif"')
    .replace(/font-family:\s*['"]?HighchartsFont['"]?/g, 'font-family: Cairo, sans-serif');

  const svgEl = new DOMParser().parseFromString(svg, 'image/svg+xml').documentElement;
  const pdfW = Number.parseFloat(svgEl.getAttribute('width') ?? String(sourceWidth));
  const pdfH = Number.parseFloat(svgEl.getAttribute('height') ?? String(chart.chartHeight));

  const RASTER_SCALE = 2;
  const canvas = await svgToCanvas(
    svg,
    Math.round(pdfW) * RASTER_SCALE,
    Math.round(pdfH) * RASTER_SCALE
  );
  const pngData = canvas.toDataURL('image/png');

  const { jsPDF } = (globalThis as Record<string, unknown>).jspdf as { jsPDF: any };
  const pdf = new jsPDF({
    orientation: pdfW >= pdfH ? 'l' : 'p',
    unit: 'px',
    format: [pdfW, pdfH],
    hotfixes: ['px_scaling']
  });
  pdf.addImage(pngData, 'PNG', 0, 0, pdfW, pdfH);
  pdf.save(`${filename}.pdf`);
};

export const exportChartImage = async ({
  chart,
  type,
  filename,
  options,
  exportingOptions
}: ExportChartImageParams): Promise<void> => {
  if (type === 'application/pdf') {
    await ensurePdfDeps();
  }
  chart.exportChart({ type, filename, ...exportingOptions }, options);
};
